import { useEffect } from 'react';
import { useFormState, useFormApi } from 'informed';
import { addDays } from 'date-fns';
import { useDateNow, useReduxState } from 'hooks';
import { getFormattedDate } from 'lib/common';
import { MandatoryReasonsTooltip } from 'components/common';
import {
  Datepicker,
  FormGroup,
  NumberStepper,
  RadioGroup,
  Select,
  Switch
} from 'components/common/form';

import type { Deadline } from 'shared-types';

interface Props {
  settings?: {
    deadlineDate?: string;
    deadlineDurationValue?: string | number;
    deadlineDurationUnit?: string;
    deadlineType?: Deadline['deadlineType'];
  };
  courseDefaultHint?: string;
  helpText?: string;
  id?: number | string;
  includeCourseDefaultOption?: boolean;
  includeCourseDefaultValue?: boolean;
  includeMandatoryOption?: boolean;
  includeSpecificDateOption?: boolean;
  label?: string;
  mandatoryDisabledReasons?: string[];
}

const TargetSchedule = ({
  settings,
  courseDefaultHint,
  helpText,
  id = 0,
  includeCourseDefaultOption = false,
  includeCourseDefaultValue = true,
  includeMandatoryOption = false,
  includeSpecificDateOption = true,
  label,
  mandatoryDisabledReasons = []
}: Props) => {
  const formApi = useFormApi();
  const formState = useFormState();

  const { dateNow: now } = useDateNow();

  const { messages, theme } = useReduxState();

  useEffect(() => {
    if (!formApi) return;

    const deadlineType = formApi.getValue('deadlineType');
    if (deadlineType !== 'date') {
      formApi.setValue('deadlineDate', addDays(now, 7));
    }
  }, [formApi, now]);

  return (
    <>
      {includeMandatoryOption ? (
        <FormGroup
          label={messages.commonMandatory}
          style={{
            fontSize: '1.5rem',
            marginBottom: 25,
            maxWidth: 'fit-content'
          }}
        >
          <MandatoryReasonsTooltip reasonTexts={mandatoryDisabledReasons}>
            <div {...(mandatoryDisabledReasons.length ? { tabIndex: 0 } : {})}>
              <Switch
                disabled={mandatoryDisabledReasons.length > 0}
                field="isMandatory"
                id={`is-mandatory-${id}`}
                label={messages.courseMandatorySet}
              />
            </div>
          </MandatoryReasonsTooltip>
        </FormGroup>
      ) : null}

      <FormGroup
        label={label || messages.courseTargetSchedule}
        style={{ fontSize: '1.5rem' }}
        {...(helpText ? { labelHelpText: helpText } : {})}
      >
        <RadioGroup
          field="deadlineType"
          spacing="8px"
          options={[
            ...(includeCourseDefaultOption
              ? [
                  {
                    id: `deadline-type-settings-default-${id}`,
                    label: (
                      <span>
                        {messages.commonDefaultValue}
                        {includeCourseDefaultValue ? (
                          <span style={{ color: theme.textColorLighter }}>
                            {' ('}
                            {settings.deadlineType === 'date' &&
                            settings.deadlineDate
                              ? getFormattedDate({
                                  date: settings.deadlineDate
                                })
                              : settings.deadlineType === 'duration' &&
                                settings.deadlineDurationValue &&
                                settings.deadlineDurationUnit
                              ? `${settings.deadlineDurationValue} ` +
                                `${
                                  {
                                    days: messages.commonDays,
                                    weeks: messages.commonWeeks,
                                    months: messages.commonMonths
                                  }[settings.deadlineDurationUnit]
                                }`
                              : messages.courseTargetScheduleNone}
                            {')'}
                          </span>
                        ) : null}
                        {courseDefaultHint ? (
                          <span
                            style={{
                              color: theme.textColorLighter,
                              display: 'block',
                              fontSize: '1.3rem'
                            }}
                          >
                            {courseDefaultHint}
                          </span>
                        ) : null}
                      </span>
                    ),
                    value: 'course-default'
                  }
                ]
              : []),
            {
              id: `deadline-type-none-${id}`,
              label: messages.courseTargetScheduleNone,
              value: 'none'
            },
            ...(includeSpecificDateOption
              ? [
                  {
                    id: `deadline-type-date-${id}`,
                    label: messages.courseTargetScheduleDate,
                    labelAdditionalContent: (
                      <div className="label-content">
                        <Datepicker
                          disabled={formState.values.deadlineType !== 'date'}
                          field="deadlineDate"
                          id={`deadline-date-${id}`}
                          minDate={addDays(now, 1)}
                          style={{ width: 200 }}
                        />
                      </div>
                    ),
                    value: 'date'
                  }
                ]
              : []),
            {
              id: `deadline-time-duration-${id}`,
              label: messages.courseTargetScheduleDuration,
              labelAdditionalContent: (
                <div className="label-content">
                  <div className="number">
                    <NumberStepper
                      disabled={formState.values.deadlineType !== 'duration'}
                      field="deadlineDurationValue"
                      id={`deadline-duration-value`}
                      min={1}
                      max={1000}
                      stepValue={1}
                    />
                  </div>
                  <div className="select">
                    <Select
                      disabled={formState.values.deadlineType !== 'duration'}
                      field="deadlineDurationUnit"
                      id={`deadline-duration-unit-${id}`}
                      options={[
                        {
                          label: messages.commonDays,
                          value: 'days'
                        },
                        {
                          label: messages.commonWeeks,
                          value: 'weeks'
                        },
                        {
                          label: messages.commonMonths,
                          value: 'months'
                        }
                      ]}
                    />
                  </div>
                </div>
              ),
              value: 'duration'
            }
          ]}
        />

        <style jsx>{`
          .label-content {
            margin: 7px 0 15px;
          }
          .number {
            display: inline-block;
            margin-right: 5px;
          }
          .select {
            display: inline-block;
            width: 140px;
          }
          .select :global(.react-select__control) {
            min-height: auto;
          }
          .select :global(.react-select__value-container) {
            padding: 5px 6px 5px;
            position: relative;
            top: -1px;
          }
          .select :global(.react-select__dropdown-indicator) {
            padding: 4px;
          }
        `}</style>
      </FormGroup>
    </>
  );
};

export default TargetSchedule;
